// Stage remote media incrementally on the user's device, not as an array of
// 5 GB of Uint8Arrays in React memory. The File handed to the normal R2 upload
// pipeline retains its camera filename and can be discarded after batch import.
export const CLOUD_DIRECT_LIMIT=5_000_000_000;
export const CLOUD_MEMORY_FALLBACK=50_000_000;
const videoType=name=>/\.webm$/i.test(name)?'video/webm':'video/mp4';
const formatBytes=n=>n>=1e9?(n/1e9).toFixed(1)+' GB':(n/1e6).toFixed(0)+' MB';
const abortIfNeeded=signal=>{
 if(signal?.aborted)throw signal.reason instanceof Error?signal.reason:
  new DOMException('Cloud import cancelled.','AbortError');
};
const mediaSignature=(head,name)=>{
 if(/\.webm$/i.test(name))return head[0]===0x1a&&head[1]===0x45&&head[2]===0xdf&&head[3]===0xa3;
 return head[4]===0x66&&head[5]===0x74&&head[6]===0x79&&head[7]===0x70; // MP4 ftyp
};

/** Returns a File and an idempotent cleanup action. Handles unknown-length streams. */
export async function stageCloudResponse(response,filename,{
 signal,onProgress=()=>{},storage=globalThis.navigator?.storage,
 maxBytes=CLOUD_DIRECT_LIMIT,memoryLimit=CLOUD_MEMORY_FALLBACK
}={}){
 abortIfNeeded(signal);
 if(!response.ok)throw new Error('Cloud provider returned HTTP '+response.status+'. Check download permissions and link expiry.');
 const mime=(response.headers.get('content-type')||'').split(';')[0].toLowerCase();
 if(mime.includes('html')||mime.includes('json')||mime.includes('xml')||mime.startsWith('text/'))
  throw new Error('This is a preview or login page, not a downloadable MP4/WebM video.');
 const lengthText=response.headers.get('content-length'),total=lengthText?Number(lengthText):null;
 if(total!==null&&(!Number.isSafeInteger(total)||total<0))throw new Error('The cloud provider returned an invalid file size.');
 if(total!==null&&total>maxBytes)
  throw new Error('Cloud video is '+formatBytes(total)+'. The current maximum is '+formatBytes(maxBytes)+'.');
 if(!response.body)throw new Error('The cloud provider did not return video data.');
 let root,handle,writer,backingName,finished=false,reader;
 const release=async()=>{
  if(writer){try{await writer.abort();}catch{/* Closed writers cannot be aborted. */}writer=null;}
  if(root&&backingName){try{await root.removeEntry(backingName);}catch{/* Already removed. */}backingName=null;}
 };
 try{
  if(storage?.getDirectory){
   root=await storage.getDirectory();
   backingName='takefold-cloud-'+crypto.randomUUID()+'.part';
   handle=await root.getFileHandle(backingName,{create:true});
   writer=await handle.createWritable();
  }else if(total!==null&&total>memoryLimit){
   throw new Error('This browser cannot stage a large cloud file. Use current Chrome or Edge with enough free device storage.');
  }
  const chunks=[],header=new Uint8Array(12);
  let checked=false,headUsed=0,bytes=0;
  reader=response.body.getReader();
  while(true){
   abortIfNeeded(signal);
   const {value,done}=await reader.read();
   if(done)break;
   if(!value?.byteLength)continue;
   bytes+=value.byteLength;
   if(bytes>maxBytes)throw new Error('Cloud download exceeded the '+formatBytes(maxBytes)+' limit.');
   if(!writer&&bytes>memoryLimit)throw new Error('This browser cannot stage a cloud video over '+formatBytes(memoryLimit)+'. Use Chrome or Edge.');
   if(headUsed<12){
    const n=Math.min(12-headUsed,value.byteLength);
    header.set(value.subarray(0,n),headUsed);headUsed+=n;
    if(headUsed===12){
     checked=true;
     if(!mediaSignature(header,filename))
      throw new Error('The download is not an MP4/WebM video. Check that downloads are enabled and the link points to the file.');
    }
   }
   if(writer)await writer.write(value);
   else chunks.push(value);
   onProgress({bytes,total,percent:total?Math.min(99,Math.floor(bytes/total*100)):null});
  }
  if(!bytes||!checked)throw new Error('The cloud provider returned an empty or invalid video.');
  if(total!==null&&total!==bytes)throw new Error('Cloud download ended early ('+formatBytes(bytes)+' of '+formatBytes(total)+'). Retry the import.');
  let file;
  if(writer){
   await writer.close();writer=null;
   const backing=await handle.getFile();
   // Blob-of-Blob composition retains a file-backed handle rather than copying
   // all media into a giant application ArrayBuffer.
   file=new File([backing],filename,{type:videoType(filename)});
  }else file=new File(chunks,filename,{type:videoType(filename)});
  finished=true;
  onProgress({bytes,total:bytes,percent:100});
  return {file,cleanup:release,bytes};
 }catch(error){
  await release();
  if(error?.name==='QuotaExceededError')
   throw new Error('Not enough free device storage to stage this video. Free up disk space or import a smaller proxy.');
  if(error?.name==='NotAllowedError'||error?.name==='SecurityError')
   throw new Error('Browser storage is unavailable. Enable site storage or use a smaller MP4/WebM file.');
  throw error;
 }
 finally{try{await reader?.cancel();}catch{/* Stream may be consumed/aborted. */}
  if(!finished)await release();}
}

/** Credential-free sources need strict browser-side host restrictions as well
 * as the independent, stricter validation on the Render fallback. */
export function trustedBrowserCloudUrl(provider,raw){
 let url;try{url=new URL(raw);}catch{throw new Error('Enter a valid HTTPS cloud video URL.');}
 if(url.protocol!=='https:'||url.port||url.username||url.password)
  throw new Error('Use a normal HTTPS download URL without embedded credentials.');
 const host=url.hostname.toLowerCase();
 const trusted=provider==='dropbox'
  ?host==='www.dropbox.com'||host==='dropbox.com'||host==='dl.dropboxusercontent.com'||host.endsWith('.dropboxusercontent.com')
  :provider==='google-link'
   ?host==='drive.google.com'||host==='drive.usercontent.google.com'||host.endsWith('.googleusercontent.com')
   :provider==='frameio'
    ?host==='frame.io'||host.endsWith('.frame.io')||host.endsWith('.cloudfront.net')||
      host==='s3.amazonaws.com'||/^s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host)||
      /^[a-z0-9.-]+\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host):false;
 if(!trusted)throw new Error('The link is not from the selected cloud provider.');
 return url;
}
